
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface LearningCurriculum {
  id: string;
  title: string;
  description: string | null;
  level: 'beginner' | 'intermediate' | 'advanced';
  discipline_id: string | null;
  creator_id: string;
  status: 'draft' | 'published' | 'archived';
  estimated_duration_hours: number;
  prerequisites: string[];
  learning_objectives: string[];
  certification_available: boolean;
  enrollment_limit: number | null;
  metadata: any;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

export interface CurriculumModule {
  id: string;
  curriculum_id: string;
  title: string;
  description: string | null;
  module_order: number;
  required: boolean;
  estimated_duration_hours: number;
  prerequisites: string[];
  resources: any[];
  created_at: string;
  updated_at: string;
}

export interface ReaderEnrollment {
  id: string;
  user_id: string;
  curriculum_id: string;
  enrollment_date: string;
  status: 'active' | 'completed' | 'paused' | 'dropped';
  progress_percentage: number;
  current_module_id: string | null;
  completion_date: string | null;
  certificate_issued: boolean;
  certificate_data: any;
  notes: string | null;
  created_at: string;
  updated_at: string;
  learning_curricula?: {
    id: string;
    title: string;
    description: string;
    level: string;
    estimated_duration_hours: number;
    disciplines?: { name: string; slug: string };
  };
}

export interface ModuleProgress {
  id: string;
  enrollment_id: string;
  module_id: string;
  status: 'not_started' | 'in_progress' | 'completed';
  progress_percentage: number;
  started_at: string | null;
  completed_at: string | null;
  time_spent_seconds: number;
  created_at: string;
  updated_at: string;
}

export const useCurricula = (discipline?: string, level?: string) => {
  return useQuery({
    queryKey: ['curricula', discipline, level],
    queryFn: async () => {
      // Return mock data for now - this will be implemented when the database tables are created
      return [
        {
          id: '1',
          title: 'Introduction to Systematic Theology',
          description: 'A comprehensive introduction to the foundations of systematic theology',
          level: 'beginner' as const,
          discipline_id: '1',
          creator_id: '1',
          status: 'published' as const,
          estimated_duration_hours: 40,
          prerequisites: [],
          learning_objectives: ['Understand basic theological concepts', 'Learn systematic approach to doctrine'],
          certification_available: true,
          enrollment_limit: null,
          metadata: {},
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          published_at: new Date().toISOString(),
          profiles: { username: 'admin', full_name: 'Administrator' },
          disciplines: { name: 'Theology', slug: 'theology' }
        }
      ];
    },
  });
};

export const useCurriculum = (id: string) => {
  return useQuery({
    queryKey: ['curriculum', id],
    queryFn: async () => {
      // Return mock data for now - this will be implemented when the database tables are created
      return {
        id,
        title: 'Introduction to Systematic Theology',
        description: 'A comprehensive introduction to the foundations of systematic theology',
        level: 'beginner',
        discipline_id: '1',
        creator_id: '1',
        status: 'published',
        estimated_duration_hours: 40,
        prerequisites: [],
        learning_objectives: ['Understand basic theological concepts'],
        certification_available: true,
        enrollment_limit: null,
        metadata: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        published_at: new Date().toISOString(),
        profiles: { username: 'admin', full_name: 'Administrator' },
        disciplines: { name: 'Theology', slug: 'theology' },
        curriculum_modules: []
      };
    },
  });
};

export const useUserEnrollments = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['user-enrollments', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      // Return mock data for now - this will be implemented when the database tables are created
      return [
        {
          id: '1',
          user_id: user.id,
          curriculum_id: '1',
          enrollment_date: new Date().toISOString(),
          status: 'active' as const,
          progress_percentage: 25,
          current_module_id: '1',
          completion_date: null,
          certificate_issued: false,
          certificate_data: {},
          notes: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          learning_curricula: {
            id: '1',
            title: 'Introduction to Systematic Theology',
            description: 'A comprehensive introduction to the foundations of systematic theology',
            level: 'beginner',
            estimated_duration_hours: 40,
            disciplines: { name: 'Theology', slug: 'theology' }
          }
        }
      ] as ReaderEnrollment[];
    },
    enabled: !!user?.id,
  });
};

export const useEnrollInCurriculum = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (curriculumId: string) => {
      // Placeholder implementation - will be implemented when database functions are created
      console.log('Enrolling in curriculum:', curriculumId);
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-enrollments'] });
      toast.success('Successfully enrolled in curriculum!');
    },
    onError: (error: any) => {
      toast.error(`Enrollment failed: ${error.message}`);
    },
  });
};

export const useUpdateModuleProgress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      enrollmentId,
      moduleId,
      progressPercentage,
      timeSpentSeconds = 0
    }: {
      enrollmentId: string;
      moduleId: string;
      progressPercentage: number;
      timeSpentSeconds?: number;
    }) => {
      // Placeholder implementation - will be implemented when database functions are created
      console.log('Updating module progress:', { enrollmentId, moduleId, progressPercentage, timeSpentSeconds });
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-enrollments'] });
      toast.success('Progress updated!');
    },
    onError: (error: any) => {
      console.error('Failed to update progress:', error);
      toast.error('Failed to update progress');
    },
  });
};

export const useCreateCurriculum = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (curriculum: Omit<LearningCurriculum, 'id' | 'creator_id' | 'created_at' | 'updated_at'>) => {
      if (!user) throw new Error('User must be authenticated');

      // Placeholder implementation - will be implemented when database tables are created
      console.log('Creating curriculum:', curriculum);
      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['curricula'] });
      toast.success('Curriculum created successfully!');
    },
    onError: (error: any) => {
      toast.error(`Failed to create curriculum: ${error.message}`);
    },
  });
};
